/**
 * Escrituração fiscal de notas — entrada, saída, serviço prestado e tomado.
 *
 * A nota é o fato fiscal; o lançamento contábil nasce dela. Escriturar aqui
 * gera as partidas (despesa/receita, fornecedor/cliente e retenções) conforme
 * a regra de apropriação do cliente.
 */
import { Fragment, useEffect, useState } from 'react';
import { PageHeader, Loading, Empty } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, competenciaLabel } from '../lib/format';
import SeletorCompetencia from '../components/SeletorCompetencia';

const API = import.meta.env.VITE_API_URL || '/api';
const auth = () => {
  const t = localStorage.getItem('atrio_jwt');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const req = async (path, opts = {}) => {
  const r = await fetch(API + path, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...auth(), ...(opts.headers || {}) },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.message || d.error || 'Falha na requisição');
  return d;
};

const ABAS = [
  { id: 'SERVICO_TOMADO',   label: 'Serviços tomados',   icon: '📥', dica: 'NFS-e recebidas de fornecedores — geram despesa e retenções' },
  { id: 'SERVICO_PRESTADO', label: 'Serviços prestados',  icon: '📤', dica: 'NFS-e emitidas — geram receita e impostos a recuperar' },
  { id: 'ENTRADA',          label: 'Entradas',            icon: '📦', dica: 'NF-e de compra de produtos e insumos' },
  { id: 'SAIDA',            label: 'Saídas',              icon: '🚚', dica: 'NF-e de venda de produtos' },
];

const VAZIO = {
  numero: '', serie: '', modelo: 'NFS-e', data_emissao: new Date().toISOString().slice(0, 10),
  part_documento: '', part_nome: '', descricao: '', valor_total: '',
  ret_iss: '', ret_irrf: '', ret_inss: '', ret_pis: '', ret_cofins: '', ret_csll: '',
};

// Retenções na ordem em que se lê uma nota de serviço.
const RETENCOES = [
  ['ret_iss', 'ISS'], ['ret_irrf', 'IRRF'], ['ret_inss', 'INSS'],
  ['ret_pis', 'PIS'], ['ret_cofins', 'COFINS'], ['ret_csll', 'CSLL'],
];

/** Documento do participante: CNPJ ou CPF, formatado pelo tamanho. */
function fmtDoc(v) {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return v || '—';
}

/** Rótulo e valor do painel de detalhe. Valor ausente vira travessão. */
function Campo({ r, v, mono, quebra }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '2px 0', alignItems: 'baseline' }}>
      <span className="small muted" style={{ flex: '0 0 40%' }}>{r}</span>
      <span style={{ flex: 1, fontSize: 12.5,
        fontFamily: mono ? 'var(--font-data, monospace)' : 'inherit',
        wordBreak: quebra ? 'break-word' : 'normal' }}>{v || '—'}</span>
    </div>
  );
}

export default function NotasFiscais({ clienteId }) {
  // Linha aberta: a nota inteira, em vez de vinte colunas pela metade.
  const [aberta, setAberta] = useState(null);
  const [aba, setAba] = useState('SERVICO_TOMADO');
  const [comp, setComp] = useState(mesAtual());
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(VAZIO);
  const [formAberto, setFormAberto] = useState(false);
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    req(`/notas-fiscais/${clienteId}?tipo=${aba}&competencia=${comp}`)
      .then(setDados).catch(e => setErro(e.message)).finally(() => setLoading(false));
  };
  useEffect(carregar, [clienteId, aba, comp]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const num = (v) => Number(String(v).replace(',', '.')) || 0;

  const salvar = async () => {
    setErro(null); setMsg(null);
    try {
      await req(`/notas-fiscais/${clienteId}`, {
        method: 'POST',
        body: JSON.stringify({
          tipo: aba, modelo: form.modelo, numero: form.numero, serie: form.serie,
          data_emissao: form.data_emissao, part_documento: form.part_documento,
          part_nome: form.part_nome, descricao: form.descricao,
          valor_total: num(form.valor_total), valor_base: num(form.valor_total),
          ret_iss: num(form.ret_iss), ret_irrf: num(form.ret_irrf), ret_inss: num(form.ret_inss),
          ret_pis: num(form.ret_pis), ret_cofins: num(form.ret_cofins), ret_csll: num(form.ret_csll),
        }),
      });
      setForm(VAZIO); setFormAberto(false); setMsg('Nota registrada.'); carregar();
    } catch (e) { setErro(e.message); }
  };

  const escriturar = async (id) => {
    setErro(null); setMsg(null);
    try {
      const r = await req(`/notas-fiscais/${clienteId}/${id}/escriturar`, { method: 'POST' });
      setMsg(`Lançamento contábil gerado a partir da nota (${r.partidas.length} partidas).`);
      carregar();
    } catch (e) { setErro(e.message); }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" sub="Use o seletor no topo." />;

  const abaAtual = ABAS.find(a => a.id === aba);
  const resumo = (dados?.resumo || []).find(r => r.tipo === aba);
  const itens = dados?.items || [];
  const servico = aba.startsWith('SERVICO');

  return (
    <>
      <PageHeader
        title="Notas fiscais"
        sub="A nota é o fato fiscal — o lançamento contábil nasce dela"
        actions={<button onClick={() => setFormAberto(v => !v)}>
          {formAberto ? '▴ Fechar' : '▾ Registrar nota'}
        </button>}
      />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {ABAS.map(a => (
          <button key={a.id} onClick={() => setAba(a.id)} title={a.dica}
            style={{ padding: '9px 16px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12.5, fontWeight: aba === a.id ? 700 : 500,
              border: aba === a.id ? '1px solid var(--gold)' : '1px solid var(--border-strong)',
              background: aba === a.id ? 'var(--gold-pale)' : 'transparent',
              color: aba === a.id ? 'var(--gold)' : 'var(--text-secondary)' }}>
            {a.icon} {a.label}
          </button>
        ))}
        <SeletorCompetencia valor={comp} onChange={setComp} disponiveis={dados?.competencias} />
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-6px 0 14px' }}>{abaAtual.dica}</p>

      {msg && <div className="card mb-2" style={{ borderLeft: '3px solid #22C55E' }}>{msg}</div>}
      {erro && <div className="card mb-2" style={{ borderLeft: '3px solid #EF4444' }}>{erro}</div>}

      {formAberto && (
        <div className="card mb-2">
          <div className="card__title">Nova nota — {abaAtual.label}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginTop: 10 }}>
            <label>Modelo<input value={form.modelo} onChange={e => set('modelo', e.target.value)} /></label>
            <label>Número<input value={form.numero} onChange={e => set('numero', e.target.value)} /></label>
            <label>Série<input value={form.serie} onChange={e => set('serie', e.target.value)} /></label>
            <label>Emissão<input type="date" value={form.data_emissao} onChange={e => set('data_emissao', e.target.value)} /></label>
            <label>CNPJ/CPF<input value={form.part_documento} onChange={e => set('part_documento', e.target.value)} /></label>
            <label style={{ gridColumn: 'span 2' }}>
              {aba === 'ENTRADA' || aba === 'SERVICO_TOMADO' ? 'Fornecedor' : 'Cliente'}
              <input value={form.part_nome} onChange={e => set('part_nome', e.target.value)} />
            </label>
            <label style={{ gridColumn: 'span 2' }}>Descrição<input value={form.descricao} onChange={e => set('descricao', e.target.value)} /></label>
            <label>Valor total<input value={form.valor_total} onChange={e => set('valor_total', e.target.value)} placeholder="0,00" /></label>
            {servico && <>
              <label>ISS retido<input value={form.ret_iss} onChange={e => set('ret_iss', e.target.value)} placeholder="0,00" /></label>
              <label>IRRF<input value={form.ret_irrf} onChange={e => set('ret_irrf', e.target.value)} placeholder="0,00" /></label>
              <label>INSS<input value={form.ret_inss} onChange={e => set('ret_inss', e.target.value)} placeholder="0,00" /></label>
              <label>PIS<input value={form.ret_pis} onChange={e => set('ret_pis', e.target.value)} placeholder="0,00" /></label>
              <label>COFINS<input value={form.ret_cofins} onChange={e => set('ret_cofins', e.target.value)} placeholder="0,00" /></label>
              <label>CSLL<input value={form.ret_csll} onChange={e => set('ret_csll', e.target.value)} placeholder="0,00" /></label>
            </>}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="ghost" onClick={() => { setForm(VAZIO); setFormAberto(false); }}>Cancelar</button>
            <button className="primary" onClick={salvar} disabled={!form.numero || !form.part_nome || !form.valor_total}>
              Registrar nota
            </button>
          </div>
        </div>
      )}

      {resumo && (
        <div className="kpi-grid kpi-grid--compact" style={{ marginBottom: 14 }}>
          <div className="kpi-card"><p className="kpi-card__label">Notas em {competenciaLabel(comp)}</p>
            <p className="kpi-card__value">{resumo.qtd}</p></div>
          <div className="kpi-card"><p className="kpi-card__label">Valor total</p>
            <p className="kpi-card__value">{fmtMoeda(resumo.total)}</p></div>
          <div className="kpi-card"><p className="kpi-card__label">Retenções</p>
            <p className="kpi-card__value">{fmtMoeda(resumo.retencoes)}</p></div>
          <div className="kpi-card"><p className="kpi-card__label">A escriturar</p>
            <p className="kpi-card__value" style={{ color: Number(resumo.pendentes) > 0 ? '#F59E0B' : '#22C55E' }}>
              {resumo.pendentes}</p></div>
          {Number(resumo.canceladas) > 0 && (
            <div className="kpi-card"><p className="kpi-card__label">Canceladas</p>
              <p className="kpi-card__value" style={{ color: '#8A847D' }}>{resumo.canceladas}</p>
              <p className="kpi-card__label" style={{ marginTop: 2 }}>{fmtMoeda(resumo.valor_canceladas)} fora do total</p></div>
          )}
        </div>
      )}

      {loading ? <Loading /> : itens.length === 0 ? (
        <Empty title={`Nenhuma nota de ${abaAtual.label.toLowerCase()} em ${competenciaLabel(comp)}`}
               sub="Use o botão Registrar nota, ou troque a competência." />
      ) : (
        <div className="card">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 22 }}></th>
                <th>Emissão</th><th>Número</th><th>Participante</th><th>Documento</th>
                <th className="num">Valor</th><th className="num">Retenções</th>
                <th className="num">Líquido</th>
                <th>Status</th><th>Contábil</th><th style={{ width: 120 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {itens.map(n => {
                const ret = ['ret_iss','ret_irrf','ret_inss','ret_pis','ret_cofins','ret_csll']
                  .reduce((s, k) => s + Number(n[k] || 0), 0);
                const expandida = aberta === n.id;
                return (
                  <Fragment key={n.id}>
                  <tr onClick={() => setAberta(expandida ? null : n.id)}
                      style={{ cursor: 'pointer' }}
                      title="Clique para ver a chave, o documento do participante e as retenções">
                    <td className="small muted" style={{ textAlign: 'center' }}>{expandida ? '▾' : '▸'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtData(n.data_emissao)}</td>
                    <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {n.modelo} {n.numero}{n.serie ? '/' + n.serie : ''}
                    </td>
                    <td>
                      <div>{n.part_nome || '—'}</div>
                      {n.part_municipio && <div className="small muted">{n.part_municipio}</div>}
                    </td>
                    <td className="small" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDoc(n.part_documento)}
                    </td>
                    <td className="num bold">{fmtMoeda(n.valor_total)}</td>
                    <td className="num">{ret > 0 ? fmtMoeda(ret) : '—'}</td>
                    <td className="num">{fmtMoeda(Number(n.valor_total || 0) - ret)}</td>
                    <td>
                      <span className={`badge ${n.status === 'escriturada' ? 'badge--success' : n.status === 'cancelada' ? 'badge--danger' : 'badge--warning'}`}>
                        {n.status}
                      </span>
                    </td>
                    <td className="small muted">
                      {n.lancamento_id ? `lcto #${n.lcto_numero ?? n.lancamento_id}` : '—'}
                    </td>
                    <td>
                      {n.status === 'pendente' && (
                        <button className="ghost"
                          onClick={e => { e.stopPropagation(); escriturar(n.id); }}
                          title="Gera o lançamento contábil a partir desta nota">
                          ⇄ Escriturar
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandida && (
                    <tr>
                      <td colSpan={11} style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
                          <div>
                            <div className="small muted" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Documento</div>
                            <Campo r="Chave de acesso" v={n.chave} mono quebra />
                            <Campo r="Modelo / série / número" v={`${n.modelo || '—'} · ${n.serie || '—'} · ${n.numero || '—'}`} />
                            <Campo r="Competência" v={n.competencia} />
                            <Campo r="Tipo" v={n.tipo} />
                          </div>
                          <div>
                            <div className="small muted" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Participante</div>
                            <Campo r="Nome" v={n.part_nome} />
                            <Campo r="CNPJ/CPF" v={fmtDoc(n.part_documento)} mono />
                            <Campo r="Município" v={n.part_municipio} />
                            <Campo r="Discriminação" v={n.descricao} quebra />
                          </div>
                          <div>
                            <div className="small muted" style={{ textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Valores e retenções</div>
                            <Campo r="Valor total" v={fmtMoeda(n.valor_total)} mono />
                            {Number(n.valor_base || 0) > 0 && <Campo r="Base de cálculo" v={fmtMoeda(n.valor_base)} mono />}
                            {Number(n.iss_proprio || 0) > 0 && <Campo r="ISS próprio" v={fmtMoeda(n.iss_proprio)} mono />}
                            {RETENCOES.filter(([k]) => Number(n[k] || 0) > 0)
                              .map(([k, r]) => <Campo key={k} r={`(−) ${r} retido`} v={fmtMoeda(n[k])} mono />)}
                            {ret > 0 && <Campo r="Líquido" v={fmtMoeda(Number(n.valor_total || 0) - ret)} mono />}
                            <Campo r="Origem" v={n.origem} />
                            {n.observacao && <Campo r="Observação" v={n.observacao} quebra />}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
